import {
	btn,
	cardmenu,
	cards,
	column,
	comp,
	DateTime,
	Format,
	h3,
	moneycolumn,
	NumberField,
	numberfield,
	store,
	StoreRecord,
	t,
	Table,
	table,
	tbar,
	translate,
	Window
} from "@intermesh/goui";
import {MonthCombo, monthcombo} from "./MonthCombo.js";
import {client} from "@intermesh/groupoffice-core";

export class StatusReportWindow extends Window {
	private yearField: NumberField;
	private monthCombo: MonthCombo;
	private readonly sentTable: Table;
	private readonly acceptedTable: Table;
	private readonly completedTable: Table;
	private readonly totalsTable: Table;
	private bookId?: string;
	private hourCategories: string[] = [];
	private otherCategories: string[] = [];

	constructor() {
		super();

		translate.setDefaultModule("business", "finance");

		this.title = t("Status report");
		this.width = 1200;
		this.height = 960;
		this.resizable = true;
		this.maximizable = true;
		this.collapsible = true;

		const toolbar = tbar({
				cls: "border-bottom bg-high"
			},
			comp({cls: "group"},
				btn({
					icon: "chevron_left",
					allowFastClick: true,
					handler: () => {
						this.monthCombo.value = (this.monthCombo.value as number) - 1;

						void this.load(this.bookId!);
					}
				}),
				this.monthCombo = monthcombo({
					listeners: {
						setvalue: ({target, newValue}) => {
							target.previousSibling()!.disabled = newValue == 1;
							target.nextSibling()!.disabled = newValue == 12;
						},
						change: () => {
							if (this.bookId) {
								void this.load(this.bookId);
							}
						}
					}
				}),
				btn({
					icon: "chevron_right",
					allowFastClick: true,
					handler: () => {
						this.monthCombo.value = (this.monthCombo.value as number) + 1;

						void this.load(this.bookId!);
					}
				})
			),
			comp({cls: "group"},
				btn({
					icon: "chevron_left",
					allowFastClick: true,
					handler: () => {
						this.yearField.value!--;
					}
				}),
				this.yearField = numberfield({
					min: 1,
					flex: 1,
					// label: t("Year"),
					thousandsSeparator: "",
					decimals: 0,
					width: 70,
					value: (new DateTime()).getYear(),
					listeners: {
						change: () => {
							if (this.bookId) {
								void this.load(this.bookId);
							}
						},
						setvalue: () => {
							if (this.bookId) {
								void this.load(this.bookId);
							}
						}
					}
				}),
				btn({
					icon: "chevron_right",
					allowFastClick: true,
					handler: () => {
						this.yearField.value!++;
					}
				})
			)
		);

		this.sentTable = this.buildStatusTable("sent");
		this.acceptedTable = this.buildStatusTable("accepted");
		this.completedTable = this.buildStatusTable("completed");
		this.totalsTable = this.buildTotalsTable();

		const cardContainer = cards({flex: 1},
			comp({title: t("Status"), cls: "fit vbox scroll", style: {gap: "2.8rem"}, flex: 1},
				comp({cls: "vbox border-top"},
					h3({style: {marginLeft: "1rem"}, text: t("Sent")}),
					this.sentTable
				),
				comp({cls: "vbox border-top"},
					h3({style: {marginLeft: "1rem"}, text: t("In progress")}),
					this.acceptedTable
				),
				comp({cls: "vbox border-top"},
					h3({style: {marginLeft: "1rem"}, text: t("Complete")}),
					this.completedTable
				),
			),
			comp({title: t("Totals"), cls: "vbox border-top"},
				h3({style: {marginLeft: "1rem"}, text: t("Totals")}),
				this.totalsTable
			)
		);


		this.items.add(
			toolbar,
			cardmenu(),
			cardContainer
		);
	}

	public load(bookId: string) {
		this.bookId = bookId;

		const year = this.yearField.value;
		const month = this.monthCombo.value;

		const sentPromise = client.jmap('business/finance/StatusReport/statusReport', {
			bookId: bookId,
			status: "sent",
			year: year,
			month: month
		});

		const acceptedPromise = client.jmap('business/finance/StatusReport/statusReport', {
			bookId: bookId,
			status: "accepted",
			year: year,
			month: month
		});

		const completedPromise = client.jmap('business/finance/StatusReport/statusReport', {
			bookId: bookId,
			status: "complete",
			year: year,
			month: month
		});

		const totalsPromise = client.jmap('business/finance/StatusReport/statusReportTotalsPerMonth', {
			bookId: bookId,
			year: year
		});

		return Promise.all([sentPromise, acceptedPromise, completedPromise, totalsPromise])
			.then(([sentItems, acceptedItems, completedItems, totalsItems]: [ReportItem[], ReportItem[], ReportItem[], any[]]) => {
				if (sentItems.length > 0 || acceptedItems.length > 0 || completedItems.length > 0) {
					const sampleItem = sentItems[0] || acceptedItems[0] || completedItems[0];

					this.hourCategories = [];
					this.otherCategories = [];

					for (const key in sampleItem) {
						if (key.startsWith('isHoursPrice_')) {
							const categoryName = key.replace('isHoursPrice_', '');
							this.hourCategories.push(categoryName);
						} else if (key.startsWith('isOthersPrice_')) {
							const categoryName = key.replace('isOthersPrice_', '');
							this.otherCategories.push(categoryName);
						}
					}

					this.buildStatusTableColumns();
				}

				this.sentTable.store.clear();
				this.sentTable.store.loadData(sentItems);

				this.acceptedTable.store.clear();
				this.acceptedTable.store.loadData(acceptedItems);

				this.completedTable.store.clear();
				this.completedTable.store.loadData(completedItems);

				this.totalsTable.store.clear();
				this.totalsTable.store.loadData(totalsItems);

				this.insertTotalsRows();
			});
	}

	private buildStatusTableColumns() {
		const baseColumns = [
			column({
				id: "customerTo",
				header: t("Customer"),
				resizable: true,
				width: 20
			}),
			column({
				id: "reference",
				header: t("Reference"),
				resizable: true,
				width: 15
			}),
			column({
				id: "executedAt",
				header: t("Execution date"),
				resizable: true,
				width: 12,
				renderer: (v) => {
					return Format.date(v);
				}
			}),
			column({
				id: "totalPrice",
				header: t("Total price"),
				resizable: true,
				width: 11,
				renderer: (v) => Format.money(Number(v))
			})
		];

		const hourPriceColumns = this.hourCategories.map(category => {
				const displayName = category.replace(/_/g, ' ');
				return column({
					id: `isHoursPrice_${category}`,
					header: t(displayName) + " " + t("Price"),
					resizable: true,
					width: 11,
					renderer: (v) => Format.money(Number(v))
				})
			}
		);

		const otherPriceColumns = this.otherCategories.map(category => {
				const displayName = category.replace(/_/g, ' ');
				return column({
					id: `isOthersPrice_${category}`,
					header: t(displayName) + " " + t("Price"),
					resizable: true,
					width: 11,
					renderer: (v) => Format.money(Number(v))
				})
			}
		);

		const middleColumns = [
			column({
				id: "marginTotalPrice",
				header: t("Margin total"),
				resizable: true,
				width: 11,
				renderer: (v) => Format.money(Number(v))
			})
		];

		const hourQuantityColumns = this.hourCategories.map(category => {
				const displayName = category.replace(/_/g, ' ');
				return column({
					id: `isHoursTotal_${category}`,
					header: t(displayName) + " " + t("Total"),
					resizable: true,
					width: 9,
					renderer: (v) => Number(v).toFixed(0)
				})
			}
		);

		const allColumns = [...baseColumns, ...hourPriceColumns, ...otherPriceColumns, ...middleColumns, ...hourQuantityColumns];

		this.sentTable.columns = allColumns;
		this.acceptedTable.columns = allColumns;
		this.completedTable.columns = allColumns;
	}

	private buildStatusTable(status: string) {
		return table({
			stateId: `finance-status-report-${status}-table`,
			cls: "border-top bg-lowest",
			store: store(),
			columns: []
		});
	}

	private buildTotalsTable() {
		return table({
			stateId: "finance-status-report-totals-table",
			store: store(),
			cls: "border-top bg-lowest",
			columns: [
				column({
					id: "month",
					header: t("Month"),
					resizable: true,
					renderer: (v) => {
						if (typeof (v) === "number") {
							return t("full_months")[v];
						} else {
							return v;
						}
					},
					width: 10
				}),
				column({
					id: "count",
					header: t("Document count"),
					resizable: true,
					width: 13
				}),
				moneycolumn({
					id: "sent",
					header: t("Sent"),
					resizable: true,
					width: 11
				}),
				moneycolumn({
					id: "accepted",
					header: t("In progress"),
					resizable: true,
					width: 11
				}),
				moneycolumn({
					id: "complete",
					header: t("Complete"),
					resizable: true,
					width: 11
				}),
				moneycolumn({
					id: "total",
					header: t("Total"),
					resizable: true,
					width: 11
				})
			]
		})
	}

	private insertTotalsRows() {
		const tables = [this.sentTable, this.acceptedTable, this.completedTable];

		tables.forEach(table => {
			const data = table.store.data;
			if (data.length === 0) {
				return;
			}

			const totals: { [key: string]: number } = {
				rowCount: 0,
				totalPrice: 0,
				marginTotalPrice: 0
			};

			this.hourCategories.forEach(category => {
				totals[`isHoursPrice_${category}`] = 0;
				totals[`isHoursTotal_${category}`] = 0;
			});

			this.otherCategories.forEach(category => {
				totals[`isOthersPrice_${category}`] = 0;
			});

			data.forEach((item: StoreRecord) => {
				totals.rowCount++;
				totals.totalPrice += Number(item.totalPrice) || 0;
				totals.marginTotalPrice += Number(item.marginTotalPrice) || 0;

				this.hourCategories.forEach(category => {
					totals[`isHoursPrice_${category}`] += Number(item[`isHoursPrice_${category}`]) || 0;
					totals[`isHoursTotal_${category}`] += Number(item[`isHoursTotal_${category}`]) || 0;
				});

				this.otherCategories.forEach(category => {
					totals[`isOthersPrice_${category}`] += Number(item[`isOthersPrice_${category}`] || 0);
				});
			});

			const totalRow: ReportItem = {
				customerTo: t("Totals"),
				reference: "",
				executedAt: "",
				totalPrice: totals.totalPrice,
				marginTotalPrice: totals.marginTotalPrice
			};

			this.hourCategories.forEach(category => {
				totalRow[`isHoursPrice_${category}`] = totals[`isHoursPrice_${category}`];
				totalRow[`isHoursTotal_${category}`] = totals[`isHoursTotal_${category}`];
			});

			this.otherCategories.forEach(category => {
				totalRow[`isOthersPrice_${category}`] = totals[`isOthersPrice_${category}`];
			})

			table.store.add(totalRow);
		});

		const totalsData = this.totalsTable.store.data;
		if (totalsData.length > 0) {
			const yearTotals = {
				month: t("Total"),
				count: 0,
				sent: 0,
				accepted: 0,
				complete: 0,
				total: 0
			};

			totalsData.forEach((item: StoreRecord) => {
				yearTotals.count += Number(item.count) || 0;
				yearTotals.sent += Number(item.sent) || 0;
				yearTotals.accepted += Number(item.accepted) || 0;
				yearTotals.complete += Number(item.complete) || 0;
				yearTotals.total += Number(item.total) || 0;
			});

			this.totalsTable.store.add(yearTotals);
		}
	}
}

interface ReportItem extends StoreRecord {
	customerTo: string,
	reference: string,
	executedAt: string
	totalPrice: number,
	marginTotalPrice: number,

	[key: string]: any  // allow dynamic properties
}